import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  Input,
  Inject,
  Optional,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, interval, switchMap, takeUntil, filter } from 'rxjs';
import {
  ChatModule,
  Message,
  SendMessageEvent,
  User,
} from '@progress/kendo-angular-conversational-ui';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { ChartsModule } from '@progress/kendo-angular-charts';
import { MarkdownModule, provideMarkdown } from 'ngx-markdown';
import {
  AskRynConversation,
  AskRynContext,
  AskRynMessage,
  AskRynService,
} from '../../services/ask-ryn.service';
import { AskRynUiService } from '../../services/ask-ryn-ui.service';
import {
  AskRynWalkthrough,
  AskRynWalkthroughService,
  splitWalkthroughsByRoute,
} from '../../services/ask-ryn-walkthrough.service';
import { collectHelpAnchors } from '@ups/xplat/base/in-app-help';
import { AskRynRelativeTimePipe } from './ask-ryn-relative-time.pipe';
import {
  ParsedAssistantContent,
  parseAssistantContent,
} from './assistant-content';
import {
  ASK_RYN_WORKBENCH_SHARE,
  AskRynWorkbenchShare,
} from './ask-ryn-workbench-share';

/**
 * Shown inline when an Ask Ryn bridge call fails. The bridge `/conversations`
 * endpoints can legitimately reject the browser (HTTP 401) while their
 * browser-facing auth is still being wired up. Every Ask Ryn HTTP call MUST
 * handle its own error so it degrades to this message instead of bubbling to
 * the app-wide GlobalErrorHandler, whose 401 branch clears the user's tokens
 * and reloads the whole page (that reload is what made clicking Ryn appear to
 * "try and reload"). Reloading is correct for a real Auth0 session expiry, not
 * for a 401 from this feature's not-yet-authed endpoint.
 */
export const ASK_RYN_UNAVAILABLE = `Ask Ryn isn't available right now. Please try again later.`;

@Component({
  selector: 'ups-ask-ryn',
  imports: [
    CommonModule,
    FormsModule,
    ChatModule,
    ButtonModule,
    IndicatorsModule,
    InputsModule,
    ChartsModule,
    MarkdownModule,
    AskRynRelativeTimePipe,
  ],
  // Self-contained Markdown provider (same pattern as page-help-dialog and
  // knowledge-base) so Ask Ryn renders rich replies in any app that mounts the
  // shared layout host, without depending on an app-root provider. Default
  // (sanitized) config — receiver content is Ryn's own bridge output, but the
  // sanitizer stays on as defense-in-depth.
  providers: [provideMarkdown()],
  templateUrl: './ask-ryn.component.html',
  styleUrls: ['./ask-ryn.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AskRynComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  /** Security user ID of the logged-in person. */
  @Input() userId = '';

  /** Application identifier (e.g., "web-software-security"). */
  @Input() appId = '';

  /** Display name for the user in the chat. */
  @Input() userName = 'You';

  /** The logged-in user's real display name, sent to the bridge so the Ask Ryn
   * -> Teams mirror shows who asked. Distinct from userName (the local chat
   * label); empty when unknown, and the bridge falls back to a generic label. */
  @Input() userDisplayName = '';

  /** Current page URL, injected by the host. */
  @Input() pageUrl = '';

  /** Current page's user help content. */
  @Input() pageHelpContent = '';

  /** Current page's technical help content. */
  @Input() technicalHelpContent = '';

  /** App display name. */
  @Input() appName = '';

  /** Ticked every 30s (ngOnInit) so OnPush relative-time labels keep ageing. */
  readonly now = signal(Date.now());

  readonly conversations = signal<AskRynConversation[]>([]);
  /** Published walkthroughs available for this app, shown as a launch list. */
  readonly walkthroughs = signal<AskRynWalkthrough[]>([]);
  /** Current route, so the list can bucket tours by the page the user is on. */
  readonly currentRoute = signal('');
  /** Walkthroughs split into "on this page" first, then the rest of the app. */
  readonly walkthroughSections = computed(() =>
    splitWalkthroughsByRoute(this.walkthroughs(), this.currentRoute())
  );
  readonly activeConversation = signal<AskRynConversation | null>(null);
  readonly feed = signal<Message[]>([]);
  readonly isLoading = signal(false);
  readonly isSending = signal(false);
  /** True from when the user sends until Ryn's reply arrives, so a typing
   * indicator shows for the whole compose window (the POST returns long before
   * the specialist's reply lands via the poll). */
  readonly isAwaitingReply = signal(false);

  /** Sentinel text marking the transient "Ryn is thinking" bubble. The receiver
   * template branches on it to render the animated spinner instead of Markdown.
   * Internal-only: it's produced solely by displayFeed (never sent to the bridge
   * or rendered from server content), so it cannot collide with a real reply. */
  readonly TYPING_SENTINEL = '__RYN_TYPING_BUBBLE__';

  /** The feed actually bound to <kendo-chat>. While awaiting a reply we append a
   * transient receiver bubble so the typing indicator appears IN the message
   * stream (where the reply will land), not below Kendo's compose box. `feed()`
   * stays server-truth, so reply-detection (clearTypingIfReplied) is unaffected. */
  readonly displayFeed = computed<Message[]>(() =>
    this.isAwaitingReply()
      ? [
          ...this.feed(),
          {
            id: 'ryn-typing',
            author: this.rynUser,
            text: this.TYPING_SENTINEL,
            timestamp: new Date(),
          },
        ]
      : this.feed()
  );
  /** Non-null when a bridge call failed; surfaced inline (never reloads). */
  readonly errorMsg = signal<string | null>(null);

  /** The title a conversation is born with server-side; the local auto-title
   * mirror below only fires while the list entry still wears it, matching the
   * bridge's own first-user-message rule (US 30685). */
  readonly DEFAULT_TITLE = 'New conversation';

  /** Server cap for a rename (mirrors the bridge's PATCH validation). */
  readonly MAX_TITLE_LENGTH = 120;

  /** Id of the conversation whose title is being edited inline, or null. */
  readonly editingConversationId = signal<string | null>(null);

  /** Draft text of the inline title editor. */
  readonly editingTitleDraft = signal('');

  readonly chatUser: User = { id: 'user', name: 'You' };
  readonly rynUser: User = { id: 'ryn', name: 'Ryn' };

  private pollingSub: Subject<void> | null = null;

  /** Id of the assistant message whose Action was last surfaced, so polling
   * doesn't re-fire the same fly/walkthrough on every 3s tick. */
  private lastActionMsgId: string | null = null;

  /** Number of Ryn (assistant) messages in the feed when the user last sent.
   * The poll clears the typing indicator once a newer assistant reply lands. */
  private assistantCountAtSend = 0;

  constructor(
    private readonly askRynService: AskRynService,
    protected readonly askRynUi: AskRynUiService,
    private readonly walkthroughService: AskRynWalkthroughService,
    private readonly router: Router,
    /** The host's Open in Ryn Workbench link for ```workbench blocks (WI 37362); absent means no link. */
    @Optional()
    @Inject(ASK_RYN_WORKBENCH_SHARE)
    protected readonly workbenchShare: AskRynWorkbenchShare | null = null
  ) {}

  ngOnInit(): void {
    this.chatUser.name = this.userName;
    this.currentRoute.set(this.router.url);
    interval(30_000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.now.set(Date.now()));
    this.loadConversations();
    this.loadWalkthroughs();
    // The host keeps this panel mounted across SPA navigation, so ngOnInit fires
    // only once. On each route change, update the current route (re-buckets the
    // "on this page" section) and re-fetch the list so it stays current (e.g. a
    // newly published tour) without a full page reload.
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((e) => {
        this.currentRoute.set(e.urlAfterRedirects);
        this.loadWalkthroughs();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadConversations(): void {
    if (!this.userId || !this.appId) return;
    this.askRynService
      .getConversations(this.userId, this.appId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (convs) => {
          this.conversations.set(convs);
          this.errorMsg.set(null);
        },
        // Handle locally so a bridge 401 never reaches the GlobalErrorHandler's
        // token-clear-and-reload branch (see ASK_RYN_UNAVAILABLE note).
        error: () => this.errorMsg.set(ASK_RYN_UNAVAILABLE),
      });
  }

  /**
   * Load the published walkthroughs for this app into the launch list. `appId`
   * is the security app GUID (environment.security.appId), which is exactly what
   * the byApp endpoint keys on. Fails QUIET: walkthroughs are a secondary aid, so
   * a fetch failure leaves an empty list and never blocks chat with an error
   * banner (the chat surfaces its own errors).
   */
  loadWalkthroughs(): void {
    if (!this.appId) return;
    this.walkthroughService
      .getPublishedWalkthroughs(this.appId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list) => this.walkthroughs.set(list ?? []),
        error: () => this.walkthroughs.set([]),
      });
  }

  /**
   * Launch a guided walkthrough. Publishes a `walkthrough` action that the
   * in-app-help wren consumes to start the tour at step 0, then closes the chat
   * panel so the full-page walkthrough is unobscured.
   */
  launchWalkthrough(walkthrough: AskRynWalkthrough): void {
    this.askRynUi.setPendingAction({
      kind: 'walkthrough',
      walkthroughId: walkthrough.id,
    });
    this.askRynUi.close();
  }

  selectConversation(conv: AskRynConversation): void {
    this.isAwaitingReply.set(false);
    this.activeConversation.set(conv);
    this.loadMessages(conv.Id);
    this.startPolling(conv.Id);
  }

  createNewConversation(): void {
    this.askRynService
      .createConversation(this.userId, this.appId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (conv) => {
          this.conversations.update((list) => [conv, ...list]);
          this.selectConversation(conv);
          this.errorMsg.set(null);
        },
        error: () => this.errorMsg.set(ASK_RYN_UNAVAILABLE),
      });
  }

  loadMessages(conversationId: string): void {
    this.isLoading.set(true);
    this.askRynService
      .getMessages(conversationId, this.appId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (msgs) => {
          this.feed.set(this.toKendoMessages(msgs));
          this.surfacePendingAction(msgs);
          this.isLoading.set(false);
          this.errorMsg.set(null);
        },
        error: () => {
          this.isLoading.set(false);
          this.errorMsg.set(ASK_RYN_UNAVAILABLE);
        },
      });
  }

  sendMessage(event: SendMessageEvent): void {
    const conv = this.activeConversation();
    if (!conv) return;

    const text = event.message.text ?? '';
    if (!text.trim()) return;

    // Add user message to feed immediately
    this.feed.update((feed) => [
      ...feed,
      { id: Date.now(), author: this.chatUser, text, timestamp: new Date() },
    ]);

    this.isSending.set(true);
    // Capture how many Ryn replies exist now; show the typing indicator for the
    // whole compose window. The poll clears it once a newer reply arrives.
    const replies = this.feed().filter((m) => m.author === this.rynUser);
    this.assistantCountAtSend = replies.length;
    const at = replies.at(-1)?.timestamp; // only a newer reply may change a grid
    const help = this.askRynUi.pageHelp(this.pageHelpContent, conv.Id, at);
    this.isAwaitingReply.set(true);

    const context: AskRynContext = {
      userId: this.userId,
      appId: this.appId,
      pageUrl: this.pageUrl,
      appName: this.appName,
      userDisplayName: this.userDisplayName,
      pageHelpContent: help,
      technicalHelpContent: this.technicalHelpContent,
      // The page's live anchor inventory, so a responder can author an
      // on-request tour grounded on elements that actually exist here.
      pageAnchors: collectHelpAnchors(),
    };

    this.askRynService
      .sendMessage(conv.Id, text, context)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSending.set(false);
          // Mirror the bridge's auto-title rule locally: ONLY the first user
          // message titles the thread. The old unconditional rewrite renamed
          // the conversation to the LATEST message on every send, which is how
          // titles "got lost" within a session (US 30685).
          this.conversations.update((list) =>
            list.map((c) =>
              c.Id === conv.Id
                ? {
                    ...c,
                    Title:
                      c.Title === this.DEFAULT_TITLE
                        ? text.length > 60
                          ? `${text.substring(0, 57)}...`
                          : text
                        : c.Title,
                    UpdatedAt: new Date().toISOString(),
                  }
                : c
            )
          );
        },
        error: () => {
          this.isSending.set(false);
          this.isAwaitingReply.set(false);
          this.errorMsg.set(ASK_RYN_UNAVAILABLE);
        },
      });
  }

  /** Open the inline title editor for a conversation (pencil button). */
  startRename(conv: AskRynConversation, event: Event): void {
    event.stopPropagation();
    this.editingConversationId.set(conv.Id);
    this.editingTitleDraft.set(
      conv.Title === this.DEFAULT_TITLE ? '' : conv.Title
    );
  }

  cancelRename(event?: Event): void {
    event?.stopPropagation();
    this.editingConversationId.set(null);
    this.editingTitleDraft.set('');
  }

  /** Persist the inline rename. The bridge records it as a user-sovereign
   * title (agents never overwrite it). Renames do not reorder the list. */
  saveRename(event?: Event): void {
    event?.stopPropagation();
    const conversationId = this.editingConversationId();
    if (!conversationId) return;
    const title = this.editingTitleDraft().trim();
    if (!title || title.length > this.MAX_TITLE_LENGTH) return;

    this.askRynService
      .renameConversation(conversationId, this.userId, this.appId, title)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updated) => {
          this.conversations.update((list) =>
            list.map((c) =>
              c.Id === conversationId
                ? {
                    ...c,
                    Title: updated.Title,
                    TitleSource: updated.TitleSource ?? 'user',
                  }
                : c
            )
          );
          this.cancelRename();
          this.errorMsg.set(null);
        },
        error: () => this.errorMsg.set(ASK_RYN_UNAVAILABLE),
      });
  }

  private startPolling(conversationId: string): void {
    // Stop any existing polling
    this.pollingSub?.next();
    this.pollingSub?.complete();
    this.pollingSub = new Subject<void>();

    interval(3000)
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.pollingSub),
        filter(() => !this.isSending()),
        switchMap(() =>
          this.askRynService.getMessages(conversationId, this.appId)
        )
      )
      .subscribe({
        next: (msgs) => {
          const newFeed = this.toKendoMessages(msgs);
          // Only update if message count changed (avoid flicker)
          if (newFeed.length !== this.feed().length) {
            this.feed.set(newFeed);
            // A new message usually means the bridge just re-titled the thread
            // (auto-title on the first message; summary-ryn's refinement after
            // the first reply), so refresh the list to pick the title up.
            // Skipped while an inline rename is open so the editor's list row
            // is not replaced under the user's cursor.
            if (this.editingConversationId() === null) {
              this.loadConversations();
            }
          }
          this.clearTypingIfReplied(newFeed);
          this.surfacePendingAction(msgs);
        },
        // Swallow background-poll failures: an error here would otherwise
        // bubble to the GlobalErrorHandler and reload the page mid-poll.
        error: () => {
          /* intentionally ignored — manual actions surface real errors */
        },
      });
  }

  /** Publish the newest reply's Action once (Id-guarded against the 3s poll) with
   * its server time, so a grid reply older than the last send is never applied. */
  private surfacePendingAction(msgs: AskRynMessage[]): void {
    const latest = [...msgs]
      .reverse()
      .find((m) => m.Role === 'assistant' && m.Action);
    const action = latest?.Action;
    if (latest && action && latest.Id !== this.lastActionMsgId) {
      this.lastActionMsgId = latest.Id;
      const { ConversationId: conv, Timestamp: at } = latest;
      this.askRynUi.setPendingAction(action, conv, at);
    }
  }

  /** Clear the typing indicator once a Ryn reply newer than the user's last
   * send has arrived (more assistant messages than were present at send time). */
  private clearTypingIfReplied(feed: Message[]): void {
    if (!this.isAwaitingReply()) return;
    const replies = feed.filter((m) => m.author === this.rynUser).length;
    if (replies > this.assistantCountAtSend) {
      this.isAwaitingReply.set(false);
    }
  }

  /** Cache of parsed assistant content keyed by raw text, so the template's
   * @let doesn't re-parse (and re-instantiate Kendo charts, causing flicker) on
   * every change-detection tick. Reply content is immutable once stored. */
  private readonly parsedCache = new Map<string, ParsedAssistantContent>();

  /** Split an assistant reply into prose, inline charts and Workbench open links
   * (assistant-content.ts). Pure + memoized; safe to call from the template. */
  parseAssistantContent(text: string): ParsedAssistantContent {
    const cached = this.parsedCache.get(text);
    if (cached) {
      return cached;
    }
    const parsed = parseAssistantContent(text, this.workbenchShare);
    this.parsedCache.set(text, parsed);
    return parsed;
  }

  private toKendoMessages(msgs: AskRynMessage[]): Message[] {
    return msgs.map((m, i) => ({
      id: m.Id ?? i,
      author: m.Role === 'user' ? this.chatUser : this.rynUser,
      text: m.Content,
      timestamp: new Date(m.Timestamp),
    }));
  }
}
